'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  LabelDefault,
  MarkdownPreview,
  MetaDefault,
  Tabs,
  Tooltip,
  getConfidenceLabel,
  isArchivedLifecycle,
} from '@terra/ui-design-system';
import { LinkSquare02Icon, Message01Icon } from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { ArchivedActionGate } from '../../common/ArchivedActionGate';
import { AssigneeSelector } from '../AssigneeSelector';
import { rowClickBlockerProps } from '../rowClickBlocker';
import {
  SIGNAL_DETAIL_TAB,
  formatSignalConfidenceLabel,
  getSignalDetailTabContent,
  resolveSignalDetailTabs,
  resolveSignalLastTestedAt,
  type SignalDetailTab,
} from './signals-list.model';
import { SignalNeoStatusIcon } from './SignalActionsCell';
import { SignalRelatedLinks } from './SignalRelatedLinks';
import { useSignalRowActions } from './useSignalRowActions';
import { useSignalsTableContext } from './SignalsTableContext';

export const SIGNAL_ROW_DETAIL_HEIGHT_PX = 320;

function SignalDetailMeta({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <HStack
      justifyContent="space-between"
      alignItems="flex-start"
      gap={2}
      minH={6}
    >
      <LabelDefault color="fg.neutral.subtle" flexShrink={0}>
        {label}
      </LabelDefault>
      <Box minW={0} textAlign="right">
        {children}
      </Box>
    </HStack>
  );
}

function SignalDetailActions({ signal }: { signal: GroupedSignal }) {
  const t = useTranslations('proxy.signals.detail');
  const { isRejectedView, navigation } = useSignalsTableContext();
  const actions = useSignalRowActions(signal);

  return (
    <HStack gap={2} flexWrap="wrap" alignItems="center" w="full" minW={0}>
      {isRejectedView ? null : (
        <>
          <Tooltip title={actions.neoTooltip}>
            <Button
              size="xs"
              variant="solid"
              flexShrink={0}
              whiteSpace="nowrap"
              disabled={actions.isNeoDisabled}
              onClick={actions.startNeo}
            >
              <SignalNeoStatusIcon status={actions.neoStatus} />
              {actions.neoLabel}
            </Button>
          </Tooltip>
          <ArchivedActionGate
            isArchived={actions.isArchived}
            tooltip={actions.archivedTooltip}
          >
            <Button
              size="xs"
              variant="outline"
              flexShrink={0}
              whiteSpace="nowrap"
              onClick={actions.sendToCopilot}
            >
              <Message01Icon size={12} />
              {actions.copilotLabel}
            </Button>
          </ArchivedActionGate>
        </>
      )}
      <Button
        size="xs"
        variant="outline"
        flexShrink={0}
        whiteSpace="nowrap"
        onClick={() =>
          navigation.openFullPageTab(navigation.getSignalTab(signal))
        }
      >
        <LinkSquare02Icon size={12} />
        {t('openFullPage')}
      </Button>
    </HStack>
  );
}

function SignalDetailContent({ signal }: { signal: GroupedSignal }) {
  const t = useTranslations('proxy.signals.detail');
  const tabs = resolveSignalDetailTabs(signal);
  const [selectedTab, setSelectedTab] = useState<SignalDetailTab | null>(null);
  const activeTab =
    selectedTab && tabs.includes(selectedTab) ? selectedTab : tabs[0];

  if (!activeTab) {
    return (
      <Box flex={1} py={2}>
        <BodyDefault color="fg.neutral.subtle">{t('noDetails')}</BodyDefault>
      </Box>
    );
  }

  return (
    <Tabs.Root
      value={activeTab}
      size="sm"
      w="full"
      flex={1}
      minH={0}
      display="flex"
      flexDirection="column"
      onValueChange={(details) =>
        setSelectedTab(tabs.find((tab) => tab === details.value) ?? null)
      }
    >
      <Tabs.List justifyContent="flex-start" h="6!" alignItems="flex-end">
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab}
            value={tab}
            h="6!"
            minW="0!"
            px="2!"
            pt="0!"
            pb="1!"
            fontSize="xs!"
            fontWeight="medium"
          >
            {t(`tabs.${tab}`)}
          </Tabs.Trigger>
        ))}
        <Tabs.Indicator />
      </Tabs.List>
      <Box flex={1} minH={0} overflowY="auto" pt={2}>
        <MarkdownPreview
          source={getSignalDetailTabContent({ signal, tab: activeTab })}
          showCodeLineNumbers={
            activeTab === SIGNAL_DETAIL_TAB.SECURITY_TEST_CASE
          }
        />
      </Box>
    </Tabs.Root>
  );
}

function SignalRelatedCard({ signal }: { signal: GroupedSignal }) {
  const t = useTranslations('proxy.signals.detail');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const dateFormatter = useDateFormatter();
  const { isRejectedView, onUpdateSignal } = useSignalsTableContext();
  const confidenceLabel = formatSignalConfidenceLabel(
    getConfidenceLabel(signal.confidence),
  );
  const lastTestedAt = resolveSignalLastTestedAt(signal);
  const isArchived = isArchivedLifecycle(signal.lifecycle);

  return (
    <VStack
      w="220px"
      h="full"
      minH={0}
      flexShrink={0}
      gap={3}
      alignItems="stretch"
      overflowY="auto"
    >
      <VStack gap={2} alignItems="stretch">
        <HStack h="6" alignItems="flex-end" pb="1">
          <LabelDefault color="fg.neutral.subtle">{t('related')}</LabelDefault>
        </HStack>
        <SignalRelatedLinks signal={signal} />
      </VStack>
      <VStack gap={1} alignItems="stretch">
        {confidenceLabel ? (
          <SignalDetailMeta label={t('confidence')}>
            <MetaDefault>{confidenceLabel}</MetaDefault>
          </SignalDetailMeta>
        ) : null}
        {isRejectedView ? null : (
          <SignalDetailMeta label={t('assignee')}>
            <ArchivedActionGate
              isArchived={isArchived}
              tooltip={tLifecycle('archivedSignalActionTooltip')}
            >
              <AssigneeSelector
                kind="signal"
                signalId={signal.id}
                selectedId={signal.assigneeId || undefined}
                disabled={isArchived}
                onAssigneeChange={(assigneeId: string | undefined) =>
                  onUpdateSignal(signal.id, {
                    assigneeId: assigneeId || null,
                  })
                }
              />
            </ArchivedActionGate>
          </SignalDetailMeta>
        )}
        <SignalDetailMeta label={t('createdAt')}>
          <MetaDefault>
            {dateFormatter.fromString(new Date(signal.createdAt).toISOString())}
          </MetaDefault>
        </SignalDetailMeta>
        {lastTestedAt ? (
          <SignalDetailMeta label={t('lastTestedAt')}>
            <MetaDefault>
              {dateFormatter.formatDistance(lastTestedAt, new Date(), {
                addSuffix: true,
              })}
            </MetaDefault>
          </SignalDetailMeta>
        ) : null}
      </VStack>
    </VStack>
  );
}

export function SignalRowDetail({ signal }: { signal: GroupedSignal }) {
  return (
    <HStack
      w="full"
      h="full"
      gap={4}
      px={4}
      py={3}
      alignItems="stretch"
      bg="bg.neutral.subtleSoft"
      cursor="default"
      whiteSpace="normal"
      userSelect="text"
      data-row-click-disabled
      onClickCapture={rowClickBlockerProps.onClickCapture}
      onClick={rowClickBlockerProps.onClick}
    >
      <VStack flex={1} minW={0} gap={3} alignItems="stretch">
        <SignalDetailContent key={signal.id} signal={signal} />
        <Box {...rowClickBlockerProps}>
          <SignalDetailActions signal={signal} />
        </Box>
      </VStack>
      <Box {...rowClickBlockerProps}>
        <SignalRelatedCard signal={signal} />
      </Box>
    </HStack>
  );
}
